'use client'

import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material'
import { BarChart, Database, Edit, Home, Loader, Sliders } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import useAuth from '../hooks/useAuth'
import { UsersApi } from '../services'
import {
  hasAdminRole,
  SYSTEM_ADMINISTRATOR_ROLE,
  switchableContextRoles,
} from '../utils/rbac'

const CarIcon = ({ size }: { size?: number }) => (
  <DirectionsCarOutlinedIcon
    // biome-ignore lint/plugin: prop-driven size adapts MUI icon to the lucide icon API
    sx={{ fontSize: size }}
  />
)

interface SidebarItemsProps {
  isCollapsed: boolean
  onItemClick?: () => void // ✅ Add this to fix TypeScript error
}

const adminRoles = [SYSTEM_ADMINISTRATOR_ROLE] as const

const items = [
  {
    icon: Home,
    title: 'Hub Home',
    path: '/homehub',
    roles: [...adminRoles, 'supplier', 'source', 'annieonly'],
    partnerTypes: ['source', 'supplier', 'Admin', 'Demo'],
  },
  {
    icon: CarIcon,
    title: 'My Claims',
    path: '/claimshire',
    roles: [SYSTEM_ADMINISTRATOR_ROLE, 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Edit,
    title: 'New Request',
    path: '/request',
    roles: [SYSTEM_ADMINISTRATOR_ROLE, 'supplier'],
    partnerTypes: ['Admin'],
  },
  {
    icon: BarChart,
    title: 'High Level Stats',
    path: '/highlevelstats',
    roles: [SYSTEM_ADMINISTRATOR_ROLE, 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Sliders,
    title: 'KPI Dashboard',
    path: '/kpidashboard',
    roles: [SYSTEM_ADMINISTRATOR_ROLE, 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Loader,
    title: 'Demo',
    path: '/demo',
    roles: [...adminRoles, 'source', 'supplier', 'annieonly'],
    partnerTypes: ['source', 'supplier', 'Admin', 'Demo'],
  },
  {
    icon: Database,
    title: 'Admin Panel',
    path: '/admin',
    roles: [SYSTEM_ADMINISTRATOR_ROLE],
    partnerTypes: ['Admin'],
  },
]

const SidebarItems: React.FC<SidebarItemsProps> = ({
  isCollapsed,
  onItemClick,
}) => {
  const pathname = usePathname()
  const {
    user: currentUser,
    effectivePartnerType,
    setEffectivePartnerType,
  } = useAuth()
  const [_isPGViewOpen, _setIsPGViewOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const hasMultipleSwitchableRoles =
    currentUser?.rolenames?.includes('source') &&
    currentUser?.rolenames?.includes('supplier')

  const isAdmin = hasAdminRole(currentUser?.rolenames)

  const canSwitchContext = hasMultipleSwitchableRoles || isAdmin

  const availableRoles = useMemo(
    () => switchableContextRoles(currentUser?.rolenames),
    [currentUser?.rolenames],
  )

  useEffect(() => {
    if (!effectivePartnerType && currentUser && availableRoles.length > 0) {
      const defaultType = currentUser.rolenames?.includes(
        SYSTEM_ADMINISTRATOR_ROLE,
      )
        ? SYSTEM_ADMINISTRATOR_ROLE
        : availableRoles[0]

      console.log(
        '🔧 SidebarItems: Setting default effectivePartnerType:',
        defaultType,
        'from roles:',
        currentUser?.rolenames,
      )
      setEffectivePartnerType(defaultType)
    }
  }, [
    effectivePartnerType,
    setEffectivePartnerType,
    currentUser,
    availableRoles,
  ])

  const handlePartnerTypeChange = async (event: SelectChangeEvent<string>) => {
    const selectedType = event.target.value || 'Demo' // ✅ Ensure it never stays null

    console.log('🔄 Updating effectivePartnerType:', selectedType)
    console.log(
      '📊 Before change - current effectivePartnerType:',
      effectivePartnerType,
    )

    setEffectivePartnerType(selectedType)

    console.log('📊 After setEffectivePartnerType - should trigger re-render')

    try {
      await new UsersApi().updateUserMe({
        requestBody: { effectivePartnerType: selectedType },
      })
      console.log('✅ Updated API with effectivePartnerType:', selectedType)
    } catch (error: any) {
      console.error('❌ Error updating partner type:', error)
    }
  }

  if (!mounted) {
    return <List />
  }

  return (
    <List>
      {items
        .filter((item) => item.roles.includes(effectivePartnerType || 'source'))
        .map(({ icon: IconComponent, title, path }) => (
          <Link
            href={path}
            key={title}
            // biome-ignore lint/plugin: next/link anchor must not underline nav items
            style={{ textDecoration: 'none' }}
          >
            <ListItemButton
              selected={pathname === path}
              // biome-ignore lint/plugin: collapse-state-driven alignment/padding; text.primary overrides anchor colour
              sx={{
                color: 'text.primary',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                padding: isCollapsed ? '8px' : '8px 16px',
              }}
              onClick={onItemClick} // ✅ Auto-close sidebar when clicking a link
            >
              <ListItemIcon
                // biome-ignore lint/plugin: collapse-state-driven icon slot width
                sx={{
                  justifyContent: 'center',
                  minWidth: isCollapsed ? 0 : 48,
                }}
              >
                <IconComponent size={isCollapsed ? 24 : 20} />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={title} />}
            </ListItemButton>
          </Link>
        ))}

      {canSwitchContext && !isCollapsed && (
        <FormControl
          fullWidth
          margin="normal"
          // biome-ignore lint/plugin: inset matches the 16px list item padding of the sidebar
          sx={{ paddingX: 2 }}
        >
          <InputLabel htmlFor="type">Context</InputLabel>
          <Select
            id="type"
            label="Context"
            value={effectivePartnerType || availableRoles[0] || 'source'}
            onChange={handlePartnerTypeChange}
          >
            {availableRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Select role context for current session
          </FormHelperText>
        </FormControl>
      )}
    </List>
  )
}

export default SidebarItems
